import { UseFieldArrayRemove } from 'react-hook-form';
import { Control } from 'react-hook-form';
import { ISaveJobForm, JobStepObjectFields } from '../../../../../common';
import { useSortable } from '@dnd-kit/sortable';
import {
    StyledDragHandle,
    StyledStepPaper,
    StyledPlaceholderBox,
    StyledSortableStepWrapper,
    StyledStepFieldsGrid,
    StyledStepActionPlaceholder,
} from '../JobInterviewSteps.styles';
import { CSS } from '@dnd-kit/utilities';

import {
    FormInputText,
    FormSelector,
    Grid,
    Icon,
    IconButton,
    Tooltip,
    useAppSelector,
    useTheme,
} from '../../../../../base';
import { userSelectors } from '../../../../../actions';
import { useTranslation } from 'react-i18next';

interface Props {
    fieldId: string;
    index: number;
    control: Control<ISaveJobForm>;
    remove: UseFieldArrayRemove;
    isFirstStep: boolean;
    isLastStep: boolean;
}

const SortableStep = ({
    fieldId,
    index,
    control,
    remove,
    isFirstStep,
    isLastStep,
}: Props) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: fieldId, disabled: isFirstStep || isLastStep });
    const companyUsersOptions = useAppSelector(userSelectors.getUsersOptions);
    const theme = useTheme();
    const { t } = useTranslation();

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative' as const,
        zIndex: isDragging ? 1 : 'auto',
    };

    const fieldNamePrefix = `steps.${index}` as const;

    return (
        <StyledSortableStepWrapper ref={setNodeRef} style={style}>
            <StyledStepPaper variant="outlined">
                {!isFirstStep && !isLastStep ? (
                    <StyledDragHandle {...attributes} {...listeners}>
                        <Icon
                            name="DragIndicatorIcon"
                            color={theme.palette.text.secondary}
                        />
                    </StyledDragHandle>
                ) : (
                    <StyledPlaceholderBox />
                )}

                <StyledStepFieldsGrid container spacing={2}>
                    <Grid item xs={3}>
                        {isFirstStep || isLastStep ? (
                            <Tooltip title={t('saveJob.stepCannotBeChanged')}>
                                <FormInputText
                                    name={`${fieldNamePrefix}.${JobStepObjectFields.STEP_NAME}`}
                                    control={control}
                                    label={t('saveJob.stepName')}
                                    placeholder={t(
                                        'saveJob.stepNamePlaceholder'
                                    )}
                                    size="small"
                                    disabled={isFirstStep || isLastStep}
                                />
                            </Tooltip>
                        ) : (
                            <FormInputText
                                name={`${fieldNamePrefix}.${JobStepObjectFields.STEP_NAME}`}
                                control={control}
                                label={t('saveJob.stepName')}
                                placeholder={t('saveJob.stepNamePlaceholder')}
                                size="small"
                                disabled={isFirstStep || isLastStep}
                            />
                        )}
                    </Grid>
                    <Grid item xs={5}>
                        <FormSelector
                            name={`${fieldNamePrefix}.${JobStepObjectFields.STEP_OWNER}`}
                            control={control}
                            label={t('saveJob.stepOwner')}
                            size="small"
                            options={companyUsersOptions}
                            disabled={isFirstStep || isLastStep}
                        />
                    </Grid>
                    {!isFirstStep && !isLastStep && (
                        <Grid item xs={3}>
                            <Tooltip title={t('saveJob.comingSoon')}>
                                <FormSelector
                                    name={`${fieldNamePrefix}.${JobStepObjectFields.INTERVIEWERS}`}
                                    control={control}
                                    label={t('saveJob.interviewers')}
                                    size="small"
                                    multiple
                                    options={companyUsersOptions}
                                    disabled
                                />
                            </Tooltip>
                        </Grid>
                    )}
                </StyledStepFieldsGrid>

                {!isFirstStep && !isLastStep ? (
                    <Tooltip title={t('saveJob.deleteStep')}>
                        <IconButton
                            onClick={() => remove(index)}
                            size="small"
                            aria-label={t('saveJob.deleteStep')}
                        >
                            <Icon name="DeleteIcon" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <StyledStepActionPlaceholder />
                )}
            </StyledStepPaper>
        </StyledSortableStepWrapper>
    );
};

export default SortableStep;
